/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginationQueryRequest } from '@vsolv/dev-kit/ngx';
import { ClaimLifecycleStep, Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, catchError, first, firstValueFrom, of, Subject } from 'rxjs';

@Injectable()
export class PolicyService {
  constructor(private http: HttpClient) {}

  private policy$ = new BehaviorSubject<Policy.Model | null>(null);

  private plan$ = new BehaviorSubject<Policy.Plan.Model | null>(null);

  private policyChanged$ = new Subject<void>();

  /** Emits whenever refreshPolicy() runs (e.g., after publish/archive/restore/edit), so views not bound to policy$ (lists) can refetch. */
  readonly policyChanged = this.policyChanged$.asObservable();
  getPolicyObservable() {
    return this.policy$.asObservable();
  }

  setPolicy(policy: Policy.Model | null) {
    this.policy$.next(policy);
  }

  async refreshPolicy(policy?: Policy.Model | string) {
    if (policy) {
      const refreshedPolicy = await this.getOne(typeof policy === 'string' ? policy : policy.id);
      this.policy$.next(refreshedPolicy);
    } else {
      if (this.policy$.value) {
        const refreshedPolicy = await this.getOne(this.policy$.value.id);
        this.policy$.next(refreshedPolicy);
      }
    }

    this.policyChanged$.next();
  }

  updateCustomerPortalConfig(props: Policy.UpdateCustomerPortalConfigRequest) {
    return this.http.post(`/api/policies/${props.policyId}/customer-portal-config`, props).pipe(first());
  }

  clearPolicy() {
    this.policy$.next(null);
  }

  async list(data: Policy.ListPoliciesQueryRequest) {
    return firstValueFrom(
      this.http
        .get<Policy.ListPoliciesQueryResponse>(`/api/policies`, {
          params: {
            page: data?.page || 1,
            limit: data?.limit || 10,
            ...(data.search && { search: data.search }),
            ...(data.status && { status: data.status }),
            ...(data.policyIds?.length && { policyIds: data.policyIds }),
            ...(data.propertySetIds?.length && { propertySetIds: data.propertySetIds }),
            ...(data.onlyLatestVersion !== undefined && { onlyLatestVersion: data.onlyLatestVersion }),
          },
        })
        .pipe(
          catchError(err => {
            console.log(err);
            throw err;
          })
        )
    );
  }

  async listForSales(
    pagination?: PaginationQueryRequest,
    propertySetIds?: string,
    status?: Policy.Status,
    policyIds?: string[],
    sessionId?: string
  ) {
    return firstValueFrom(
      this.http
        .get<Policy.ListPoliciesQueryResponse>(`/api/policies-sales`, {
          params: {
            page: pagination?.page || 1,
            limit: pagination?.limit || 10,
            ...(propertySetIds?.length && { propertySetIds }),
            ...(sessionId && { sessionId }),
            ...(status && { status }),
            ...(policyIds?.length && { policyIds }),
          },
        })
        .pipe(
          catchError(err => {
            console.log(err);
            throw err;
          })
        )
    );
  }

  async export(limit?: number) {
    return firstValueFrom(
      this.http.get<Policy.ExportPoliciesQueryResponse>(`/api/policies/export`, { params: { limit: limit || 50 } })
    );
  }

  async generateProvisionTemplate(policyId: string, planId?: string) {
    return firstValueFrom(
      this.http.get<Policy.ExportPoliciesQueryResponse>(`/api/policies/${policyId}/provision-template`, {
        params: { ...(planId && { planId }) },
      })
    );
  }

  async getOne(policyId: string) {
    return firstValueFrom(
      this.http.get<Policy.Model>(`/api/policies/${policyId}`).pipe(
        catchError(err => {
          if (err.status === 404) return of(null);
          throw err;
        })
      )
    );
  }

  async getVersions(policyId: string) {
    return firstValueFrom(
      this.http.get<Policy.RetrievePolicyVersionsQueryResponse>(`/api/policies/${policyId}/versions`)
    );
  }

  async getWarrantyCounts(policyId: string) {
    return firstValueFrom(
      this.http
        .get<Policy.GetWarrantyCountsResponse>(`/api/policies/${policyId}/warranty_counts`)
        .pipe(catchError(() => of(null)))
    );
  }

  async getStats(policyId: string, body?: Policy.RetrievePolicyStatsQueryRequest) {
    return firstValueFrom(
      this.http
        .get<Policy.RetrievePolicyStatsQueryResponse>(`/api/policies/${policyId}/stats`, {
          params: {
            ...(body?.date && { date: body.date.toString() }),
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  async create(body: Policy.CreatePolicyRequest) {
    return await firstValueFrom(this.http.post<Policy.CreatePolicyResponse>(`/api/policies`, body));
  }

  async update(policyId: string, body: Policy.UpdatePolicyRequest) {
    return await firstValueFrom(this.http.patch<Policy.UpdatePolicyResponse>(`/api/policies/${policyId}`, body));
  }

  async publish(policyId: string) {
    return await firstValueFrom(
      this.http.post<Policy.PublishPolicyResponse>(`/api/policies/${policyId}/publish`, { policyId })
    );
  }

  async archive(policyId: string) {
    return await firstValueFrom(
      this.http.post<Policy.ArchivePolicyResponse>(`/api/policies/${policyId}/archive`, { policyId })
    );
  }

  async restore(policyId: string) {
    return await firstValueFrom(
      this.http.post<Policy.RestorePolicyResponse>(`/api/policies/${policyId}/restore`, { policyId })
    );
  }

  async duplicate(policyId: string, isNewVersion: boolean) {
    const duplicated = await firstValueFrom(
      this.http.post<Policy.DuplicatePolicyResponse>(`/api/policies/${policyId}/duplicate`, { isNewVersion })
    );

    // If we’re currently viewing this policy, swap in the duplicated version so callers don’t need a full refresh
    const currentPlan = this.plan$.value;

    if (duplicated) {
      this.policy$.next(duplicated);

      if (currentPlan && duplicated.plans?.length) {
        const base = currentPlan.id.split('-')[0];
        const matchingPlan = duplicated.plans.find(p => p.id.startsWith(base));
        if (matchingPlan) {
          this.plan$.next(matchingPlan);
        }
      }
    }

    return duplicated;
  }

  migrateToLive(policyId: string) {
    return firstValueFrom(
      this.http.post<Policy.MigratePolicyFromSandboxToLiveResponse>(
        `/api/policies/${policyId}/sandbox`,
        { policyId },
        { headers: { 'Content-Type': 'application/json' } }
      )
    );
  }

  migrateToSandbox(policyId: string) {
    return firstValueFrom(
      this.http.post<Policy.MigratePolicyFromLiveToSandboxResponse>(
        `/api/policies/${policyId}/live`,
        { policyId },
        { headers: { 'Content-Type': 'application/json' } }
      )
    );
  }

  async delete(policyId: string) {
    return await firstValueFrom(this.http.delete<Policy.DeletePolicyResponse>(`/api/policies/${policyId}`));
  }

  getPlanObservable() {
    return this.plan$.asObservable();
  }

  getPlanSnapshot() {
    return this.plan$.value;
  }

  setPlan(plan: Policy.Plan.Model | null) {
    this.plan$.next(plan);
  }

  async refreshPlan(
    plan?: Policy.Plan.Model,
    opts?: {
      includeCoverageConfigs?: boolean;
      includeFees?: boolean;
      termId?: string;
      includeCoverageConfigBlocks?: boolean;
    }
  ) {
    if (plan) {
      const refreshedPlan = await this.getPlan(plan.policyId, plan.id, {
        includeCoverageConfigs: opts?.includeCoverageConfigs ?? false,
        includeFees: opts?.includeFees ?? false,
        termId: opts?.termId,
        includeCoverageConfigBlocks: opts?.includeCoverageConfigBlocks ?? false,
      });
      this.plan$.next(refreshedPlan);
    } else {
      if (this.plan$.value) {
        const refreshedPlan = await this.getPlan(this.plan$.value.policyId, this.plan$.value.id, {
          includeCoverageConfigs: opts?.includeCoverageConfigs ?? false,
          includeFees: opts?.includeFees ?? false,
          termId: opts?.termId,
          includeCoverageConfigBlocks: opts?.includeCoverageConfigBlocks ?? false,
        });
        this.plan$.next(refreshedPlan);
      }
    }
  }

  clearPlan() {
    this.plan$.next(null);
  }

  async getPlan(
    policyId: string,
    planId: string,
    opts?: {
      includeCoverageConfigs?: boolean;
      includeFees?: boolean;
      termId?: string;
      includeCoverageConfigBlocks?: boolean;
    }
  ) {
    return firstValueFrom(
      this.http
        .get<Policy.Plan.RetrievePolicyPlanQueryResponse>(`/api/policies/${policyId}/plans/${planId}`, {
          params: {
            includeCoverageConfigs: (opts?.includeCoverageConfigs ?? true).toString(),
            includeFees: (opts?.includeFees ?? true).toString(),
            ...(opts?.termId ? { termId: opts.termId } : {}),
            includeCoverageConfigBlocks: (opts?.includeCoverageConfigBlocks ?? true).toString(),
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  async getAllPlans(policyId: string): Promise<Policy.Plan.Model[]> {
    const plans: Policy.Plan.Model[] = [];

    const limit = 25;
    let page = await this.getPlans(policyId, 1, limit).catch(() => null);

    while (page && page.items.length > 0) {
      plans.push(...page.items);

      if (page.meta.currentPage < (page.meta.totalPages ?? 0)) {
        page = await this.getPlans(policyId, page.meta.currentPage + 1, limit).catch(() => null);
      } else {
        page = null;
      }
    }

    return plans;
  }

  async getPlans(policyId: string, page?: number, limit?: number) {
    return firstValueFrom(
      this.http
        .get<Policy.Plan.ListPolicyPlansQueryResponse>(`/api/policies/${policyId}/plans`, {
          params: {
            page: (page || 1).toString(),
            limit: (limit || 10).toString(),
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  async getPlanFees(policyId: string, planId: string, termId: string, pagination?: { page: number; limit: number }) {
    return firstValueFrom(
      this.http
        .get<Policy.PlanCoverageFee.ListPlanCoverageFeesQueryResponse>(
          `/api/policies/${policyId}/plans/${planId}/fees/${termId}`,
          {
            params: {
              page: pagination?.page ?? 1,
              limit: pagination?.limit ?? 100,
            },
          }
        )
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  async getPlanLiabilityGroups(policyId: string, planId: string, submissionId?: string) {
    return firstValueFrom(
      this.http
        .get<Policy.Plan.LiabilityGroup[]>(`/api/policies/${policyId}/plans/${planId}/groups`, {
          params: {
            ...(submissionId && { submissionId }),
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  async createPlan(policyId: string, body: Policy.Plan.CreatePolicyPlanRequest) {
    return await firstValueFrom(
      this.http.post<Policy.Plan.CreatePolicyPlanResponse>(`/api/policies/${policyId}/plans`, body)
    );
  }

  async duplicatePlan(policyId: string, planId: string, title: string) {
    return await firstValueFrom(
      this.http.post<Policy.Plan.DuplicatePolicyPlanCommandResponse>(
        `/api/policies/${policyId}/plans/${planId}/duplicate`,
        { title, canCreateNewVersion: true }
      )
    );
  }

  async updatePlan(planId: string, policyId: string, body: Policy.Plan.UpdatePolicyPlanRequest) {
    return await firstValueFrom(
      this.http.patch<Policy.Plan.UpdatePolicyPlanResponse>(`/api/policies/${policyId}/plans/${planId}`, body)
    );
  }

  async updatePlanTerm(
    planId: string,
    termId: string,
    policyId: string,
    body: Policy.Plan.UpdatePolicyPlanTermRequest
  ) {
    return await firstValueFrom(
      this.http.patch<Policy.Plan.UpdatePolicyPlanResponse>(`/api/policies/${policyId}/plans/${planId}/${termId}`, body)
    );
  }

  async setPlanVisibility(policyId: string, planId: string, visible: boolean) {
    return await firstValueFrom(
      this.http.patch<{ id: string }>(`/api/policies/${policyId}/plans/${planId}/set-visibility`, { visible: visible })
    );
  }

  async exportPlanCoverageConfigs(planId: string, policyId: string) {
    return await firstValueFrom(
      this.http.get<Policy.Plan.ExportPlanCoverageConfigsQueryResponse>(
        `/api/policies/${policyId}/plans/${planId}/export-coverage-configs`,
        {}
      )
    );
  }

  async deletePlan(planId: string, policyId: string) {
    return await firstValueFrom(
      this.http.delete<Policy.Plan.DeletePolicyPlanCommandResponse>(`/api/policies/${policyId}/plan/${planId}`, {
        body: { canCreateNewVersion: true },
      })
    );
  }

  async updateClaimLifecycleSteps(policyId: string, body: ClaimLifecycleStep.UpdateClaimLifecycleStepsRequest) {
    return await firstValueFrom(
      this.http.patch<ClaimLifecycleStep.UpdateClaimLifecycleStepsResponse>(
        `/api/policies/${policyId}/claim-lifecycle-steps`,
        body
      )
    );
  }

  async updateDocument(documentId: string, policyId: string, body: Policy.UpdatePolicyDocumentRequest) {
    return await firstValueFrom(
      this.http.patch<Policy.UpdatePolicyDocumentResponse>(`/api/policies/${policyId}/documents/${documentId}`, body)
    );
  }

  async updateEmail(emailId: string, policyId: string, body: Policy.UpdatePolicyEmailRequest) {
    return await firstValueFrom(
      this.http.patch<Policy.UpdatePolicyEmailResponse>(`/api/policies/${policyId}/emails/${emailId}`, body)
    );
  }

  async createEmail(policyId: string, body: Policy.CreatePolicyEmailRequest) {
    return await firstValueFrom(
      this.http.post<Policy.CreatePolicyEmailResponse>(`/api/policies/${policyId}/emails`, body)
    );
  }

  async uploadEmailAttachment(policyId: string, file: File) {
    const formData = new FormData();
    formData.set('file', file, file.name);

    return await firstValueFrom(
      this.http.post<Policy.PolicyEmailUploadedAttachment>(`/api/policies/${policyId}/email-attachments`, formData)
    );
  }

  async deleteEmailAttachment(policyId: string, storageItemId: string) {
    return await firstValueFrom(
      this.http.delete<{ id: string }>(`/api/policies/${policyId}/email-attachments/${storageItemId}`)
    );
  }

  async createCancelationRule(policyId: string, dto: Policy.CreatePolicyCancelationRuleRequest) {
    return await firstValueFrom(
      this.http.post<Policy.CreatePolicyCancelationRuleResponse>(`/api/policies/${policyId}/cancelation-rules`, dto)
    );
  }

  async updateCancelationRule(policyId: string, dto: Policy.UpdatePolicyCancelationRuleRequest) {
    return await firstValueFrom(
      this.http.patch<Policy.UpdatePolicyCancelationRuleResponse>(
        `/api/policies/${policyId}/cancelation-rules/${dto.country}/${dto.state}`,
        dto
      )
    );
  }

  async deleteCancelationRule(policyId: string, dto: Policy.DeletePolicyCancelationRuleRequest) {
    return await firstValueFrom(
      this.http.delete<Policy.DeletePolicyCancelationRuleResponse>(
        `/api/policies/${policyId}/cancelation-rules/${dto.country}/${dto.state}`
      )
    );
  }
}
